import { useContext, useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { QuestionOption, QuestionProps } from "./Props";

import InterfaceContext from "../Context";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { useLocalizedStrings } from "../Localization";
import { languageContent, safeParse } from "../Util";
import { fisherYatesShuffle, hash } from "../utils/orderRandomizer";
import { getOptionsFromProps, MarkdownLabel } from "./SingleSelect";

export function MultipleSelectQuestion(props: QuestionProps) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const metadata = safeParse(props["Metadata"] || '{}');
  const targetField = props["Target Field"]!;
  const fieldValue = props.info[props["Target Field"] || ''];

  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const hasSelectAll = props["Options (if relevant)"]?.some((o: any) => o['Select All'] === true);
  let options = getOptionsFromProps(props);

  const regex = /\$[a-zA-Z\\_][a-zA-Z\\_0-9]+/g;

  function replacer(match: string){
    const variable = match.slice(1).replace(/\\_/g,'_');

    // console.log("Found match:", variable, variable === "uid");

    if(variable === "uid") return props.uid;
    if(props.info[variable] !== undefined && props.info[variable] !== null) return props.info[variable];
    return match;
  }

  const marked = useModularMarkdown({
    content: props[languageContent(context.lang)] || '',
    info: props.info,
    replacer: str => str.replace(regex, replacer)
  });

  let seed: string | null = null;
  if (metadata?.component?.randomizeOrder && targetField) {
    seed = localStorage.getItem('ChoiceRandomizerSeed' + (props.uid || ''));
    if (seed === null) {
      seed = Math.random() + (props.uid || '');
      localStorage.setItem('ChoiceRandomizerSeed' + (props.uid || ''), seed);
    }
    options = fisherYatesShuffle(options, hash(0, seed), targetField);
  }

  useEffect(() => {
    // Determine if all options are selected and thus whether 'select all' should be checked
    // Ignore if none of the options is a 'select all' option
    if (hasSelectAll) {
      const numOptions = props["Options (if relevant)"]?.reduce((total, option) => {
        return option['Exclusive Option'] !== true && option['Select All'] !== true
          ? total + 1
          : total;
      }, 0);
      const numOptionsSelected = fieldValue?.split(',').length || 0;

      if (numOptions === numOptionsSelected) {
        if (!selectAllChecked) {
          setSelectAllChecked(true);
        }
      } else {
        if (selectAllChecked) {
          setSelectAllChecked(false);
        }
      }
    }
  }, [hasSelectAll, fieldValue]);

  function setChecked(option: QuestionOption, checked: boolean) {
    if (option['Exclusive Option'] === true) {
      if (!checked) {
        props.setInfoKey(props["Target Field"]!, '', false, false);
      } else {
        props.setInfoKey(props["Target Field"]!, option.Name, true, false);
      }
      return;
    }

    // 'Select All' option is more like a button for setting other options, not a true option
    if (option["Select All"] === true) {
      if (!checked) {
        props.setInfoKey(props["Target Field"] as string, "", false, false);
      } else {
        // If selecting all would exceed the max number of allowed selections, show an alert instead
        if ((options).length - 1 > metadata.max_allowed_selections) {
          alert(L.questions.choice.already_selected_max);
          return;
        }
        const checkedOptions = [];
        for (const option of props['Options (if relevant)']!) {
          if (option['Exclusive Option'] !== true && option['Select All'] !== true) {
            checkedOptions.push(option.Name);
          }
        }

        props.setInfoKey(props["Target Field"] as string, checkedOptions.join(","), true, false);
      }
      return;
    }

    const kind = option.Name;
    let old = (props.info[props["Target Field"]!] || "")
      .split(",")
      .filter((k: string) => {
        if (k.length === 0) return false;
        for (const o of options) {
          if (o.Name === k && o["Exclusive Option"] === true) return false;
        }
        return true;
      });
    if (checked) {
      if (old.indexOf(kind) === -1) {
        if (old.length >= metadata.max_allowed_selections) {
          // Prevent selecting more than the max number of options
          alert(L.questions.choice.already_selected_max);
          return;
        }
        old.push(kind);
        const next = old.join(",");
        props.setInfoKey(props["Target Field"]!, next, next.length > 0, false);
      }
    } else {
      // Not checked, remove from info
      if (old.indexOf(kind) >= 0) {
        old = old.filter((k: string) => k !== kind);
        const next = old.join(",");
        props.setInfoKey(props["Target Field"]!, next, next.length > 0, false);

        if(kind === 'other' || option["Other Field"]) {
          props.setInfoKey(props["Target Field"]! + '_' + kind, "", true, false);
        }
      }
    }
  }

  function isChecked(o: QuestionOption): boolean {
    if (o['Select All']) {
      return selectAllChecked;
    }
    return (props.info[props["Target Field"]!] || "")
      .split(",")
      .indexOf(o["Name"]) !== -1;
  }

  function updateOtherValue(o: QuestionOption, value: string) {
    props.setInfoKey(props["Target Field"] + '_' + o.Name, value, value.length > 0, false);
  }

  const name = props['Question'] + '_' + props["Target Field"]!;

  function generateCheckbox(o: QuestionOption): any {
    const fields: any[] = [];

    if (metadata.filter && props.info[metadata.filter] && !props.info[metadata.filter].includes(o['Name'])) {
      return fields;
    }

    fields.push(<Form.Check
      checked={isChecked(o)}
      onChange={(e: any) => setChecked(o, e.target.checked)}
      type="checkbox"
      name={name}
      id={name + o["Name"]}
      key={name + o["Name"]}
      label={ 
        <span className={`${context.textAlign === 'right' ? 'mr-4' : ''}`}>
          <MarkdownLabel text={(o as any)[languageContent(context.lang).replace('Content', 'Text')] || o.Name} />
        </span>
      } />
    );

    if((o["Name"] === "other" || o["Other Field"]) && isChecked(o)) {
      fields.push(
        <Form.Group key={name + "_" + o["Name"] + '_group'}>
          <Form.Control type="text"
            defaultValue={props.info[props['Target Field'] + '_' + o.Name]}
            onChange={(e: any) => updateOtherValue(o, e.target.value)}
            name={name + '_' + o.Name}
            key={name + '_' + o.Name}
          />
        </Form.Group>);
    }

    return fields;
  }

  return (
    <Form.Group>
      <fieldset>
        <legend>
          {marked}
        </legend>
        {options?.map((o) => {
          return generateCheckbox(o)
        })}
      </fieldset>
    </Form.Group>
  );
}